/**
 * Plain-English problems for a lead-form field, shared by the plain HTML
 * error page (plain-error.ts) and the React forms when the server refuses a
 * submission. Client-safe: no server or site imports.
 */

/** How each form field is named to a visitor, in "Please add your ___." */
export const FIELD_LABELS: Readonly<Record<string, string>> = {
  firstName: 'first name',
  lastName: 'last name',
  name: 'name',
  email: 'email address',
  phone: 'phone number',
  company: 'company or organization name',
  eventCity: 'event city',
  eventDate: 'event date',
  groupSize: 'group size',
  programSelection1: 'program choice',
  programSelection2: 'second program choice',
  message: 'message',
  eventTiming: 'event timing',
  siteUrl: 'website address',
  collaborationType: 'type of collaboration',
  topic: 'topic',
  pitch: 'pitch',
  samples: 'writing samples',
  partnerType: 'type of partner',
  partnerCity: 'city',
  details: 'details',
};

/** Why a field was refused, as far as the copy cares. */
export const FIELD_PROBLEM = {
  missing: 'missing',
  tooShort: 'tooShort',
  tooLong: 'tooLong',
  other: 'other',
} as const;
export type FieldProblem = (typeof FIELD_PROBLEM)[keyof typeof FIELD_PROBLEM];

const PROBLEM_SENTENCE: Readonly<Record<FieldProblem, (label: string) => string>> = {
  missing: (label) => `Please add your ${label}.`,
  tooShort: (label) => `Please add a little more to your ${label}.`,
  tooLong: (label) => `Please shorten your ${label}.`,
  other: (label) => `Please check your ${label}.`,
};

/** "Please add your first name." for a known field; undefined for one a visitor would not recognize. */
export function fieldSentence(field: string, problem: FieldProblem): string | undefined {
  const label = FIELD_LABELS[field];
  return label ? PROBLEM_SENTENCE[problem](label) : undefined;
}

export function isBlank(value: unknown): boolean {
  return value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
}
